/**
 * Excel-style EDATE: add `months` to a calendar date (UTC YYYY-MM-DD).
 * Clamps day-of-month to the last day of the target month.
 */
export function addMonthsIso(iso: string, months: number): string {
  const day = toIsoDate(iso);
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const targetMonthIndex = m - 1 + months;
  const year = y + Math.floor(targetMonthIndex / 12);
  const monthIndex = ((targetMonthIndex % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const clamped = Math.min(d, lastDay);
  return new Date(Date.UTC(year, monthIndex, clamped)).toISOString().slice(0, 10);
}

/**
 * Inclusive calendar-day span between two ISO timestamps/dates.
 * Used for PTO balance math (simple, Workers-safe).
 */
export function inclusiveDayCount(fromIso: string, toIso: string): number {
  const from = Date.parse(`${fromIso.slice(0, 10)}T00:00:00.000Z`);
  const to = Date.parse(`${toIso.slice(0, 10)}T00:00:00.000Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return 0;
  return Math.floor((to - from) / 86_400_000) + 1;
}

/** Advance a UTC calendar day by `delta` days. */
function addUtcDays(dayIso: string, delta: number): string {
  const ms = Date.parse(`${toIsoDate(dayIso)}T00:00:00.000Z`) + delta * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
}

/** Whether a YYYY-MM-DD falls on Saturday or Sunday (UTC). */
export function isWeekendIso(dayIso: string): boolean {
  const dow = new Date(`${toIsoDate(dayIso)}T00:00:00.000Z`).getUTCDay();
  return dow === 0 || dow === 6;
}

/**
 * Inclusive weekday count (Mon–Fri) between two ISO dates.
 * Weekends are excluded — used for PTO/UTO usage totals.
 */
export function inclusiveWeekdayCount(fromIso: string, toIso: string): number {
  let from = toIsoDate(fromIso);
  const to = toIsoDate(toIso);
  if (to < from) return 0;
  let count = 0;
  while (from <= to) {
    if (!isWeekendIso(from)) count++;
    if (from === to) break;
    from = addUtcDays(from, 1);
  }
  return count;
}

/** Year of an ISO date (UTC). */
export function isoYear(iso: string): number {
  return Number(iso.slice(0, 4));
}

/** YYYY-MM-DD from an ISO timestamp/date. */
export function toIsoDate(iso: string): string {
  return iso.slice(0, 10);
}

/** Today's UTC calendar date as YYYY-MM-DD. */
export function utcTodayIso(now = Date.now()): string {
  return new Date(now).toISOString().slice(0, 10);
}

/** Whether a calendar day falls inside an inclusive ISO date range. */
export function isoDateInRange(dayIso: string, fromIso: string, toIso: string): boolean {
  const day = toIsoDate(dayIso);
  return day >= toIsoDate(fromIso) && day <= toIsoDate(toIso);
}

/** Whether two inclusive ISO date ranges overlap on any calendar day. */
export function rangesOverlap(aFrom: string, aTo: string, bFrom: string, bTo: string): boolean {
  return toIsoDate(aFrom) <= toIsoDate(bTo) && toIsoDate(bFrom) <= toIsoDate(aTo);
}

/** Every YYYY-MM-DD day in a UTC month (`monthIndex` is 0–11). */
export function eachDayOfMonth(year: number, monthIndex: number): string[] {
  const days: string[] = [];
  for (let day = 1; ; day++) {
    const dt = new Date(Date.UTC(year, monthIndex, day));
    if (dt.getUTCMonth() !== monthIndex) break;
    days.push(dt.toISOString().slice(0, 10));
  }
  return days;
}

export type CalendarCell = {
  date: string;
  inMonth: boolean;
};

/**
 * 6×7 Sunday-start calendar grid for a UTC month, including leading/trailing
 * days from adjacent months.
 */
export function calendarGrid(year: number, monthIndex: number): CalendarCell[] {
  const firstDow = new Date(Date.UTC(year, monthIndex, 1)).getUTCDay();
  const startMs = Date.UTC(year, monthIndex, 1 - firstDow);
  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i++) {
    const dt = new Date(startMs + i * 86_400_000);
    cells.push({
      date: dt.toISOString().slice(0, 10),
      inMonth: dt.getUTCMonth() === monthIndex,
    });
  }
  return cells;
}
